import SignStoreConfig from './config'
import VaultStoreConfig from '../vault/config'
import { shouldPreserveDirectMobileFlowVaultData } from '../../components/utils/storage'

function persistDirectMobileFlowStep(state) {
  if (!shouldPreserveDirectMobileFlowVaultData()) {
    return
  }

  const enabledSteps = state.steps.filter((step) => step.isEnabled)
  const currentStep = enabledSteps[state.currentStepIndex]
  if (currentStep?.stepName) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_STEP, currentStep.stepName)
  }
}

export default {
  NEXT_STEP(state, stepNumber = null) {
    const enabledSteps = state.steps.filter((step) => step.isEnabled)
    if (stepNumber !== null) {
      // Jump to specific step number (1-based index)
      const targetIndex = stepNumber
      if (targetIndex >= 0 && targetIndex < enabledSteps.length) {
        state.currentStepIndex = targetIndex
      }
    } else {
      // Default behavior: move to the next step
      if (state.currentStepIndex < enabledSteps.length - 1) {
        state.currentStepIndex++
      }
    }
    persistDirectMobileFlowStep(state)
  },
  PREVIOUS_STEP(state) {
    if (state.currentStepIndex > 0) {
      state.currentStepIndex--
      persistDirectMobileFlowStep(state)
    }
  },
  SET_STEP(state, step) {
    const enabledSteps = state.steps.filter((s) => s.isEnabled)
    const index = enabledSteps.findIndex((s) => s.stepName === step)
    if (index !== -1) {
      state.currentStepIndex = index
      persistDirectMobileFlowStep(state)
    }
  },
  // Update the enabled state of a step
  UPDATE_STEP_STATE(state, payload) {
    const step = state.steps.find((s) => s.stepName === payload.stepName)
    if (step) {
      step.isEnabled = payload.isEnabled
    }
  },
  clearAllLocalStore() {
    localStorage.removeItem('mb-user-id')
    // The following are intentionally preserved so the user can unlock their vault
    // with PIN after a page refresh without re-logging in. Expiry is checked
    // at point-of-use via isSessionValid / isKycTokenValid getters.
    // signin.profile, vault.vaultData, signin.session,
    // signin.sessionExpiresAt, signin.kycServiceUserAccessToken

    // move these in store/vault module
    if (!shouldPreserveDirectMobileFlowVaultData()) {
      localStorage.removeItem(VaultStoreConfig.LOCAL_STATES.VAULT_DATA_RAW)
    }
    localStorage.removeItem(VaultStoreConfig.LOCAL_STATES.VAULT_LOCK)
    localStorage.removeItem('vault.vaultPin')
  },

  resetMobileHandoffState(state) {
    // The Vuex store is created before App.created(), so clearing localStorage
    // alone leaves values loaded from the previous browser session in memory.
    const preserveMobileFlowVaultData = shouldPreserveDirectMobileFlowVaultData()
    const preservedMobileFlowState = preserveMobileFlowVaultData
      ? {
          session: localStorage.getItem(SignStoreConfig.LOCAL_STATES.SESSIONS),
          mobileFlowSession: localStorage.getItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION),
          mobileFlowStep: localStorage.getItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_STEP),
          widgetConfig: localStorage.getItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG),
          widgetConfigId: localStorage.getItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG_ID),
          vaultDataRaw: localStorage.getItem(VaultStoreConfig.LOCAL_STATES.VAULT_DATA_RAW),
        }
      : null

    localStorage.clear()

    if (preservedMobileFlowState) {
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.SESSIONS, preservedMobileFlowState.session)
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION, preservedMobileFlowState.mobileFlowSession)
      if (preservedMobileFlowState.mobileFlowStep) {
        localStorage.setItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_STEP, preservedMobileFlowState.mobileFlowStep)
      }
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG, preservedMobileFlowState.widgetConfig)
      if (preservedMobileFlowState.widgetConfigId) {
        localStorage.setItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG_ID, preservedMobileFlowState.widgetConfigId)
      }
      if (preservedMobileFlowState.vaultDataRaw) {
        localStorage.setItem(VaultStoreConfig.LOCAL_STATES.VAULT_DATA_RAW, preservedMobileFlowState.vaultDataRaw)
      }
    }

    sessionStorage.removeItem('vault.vaultMnemonic')

    state.authenticationAccessToken = {}
    state.ifNewUser = false
    state.cosmosConnection = {}
    state.widgetConfigFromDb = null
    state.steps = []
    state.schemaIds = {}
    state.idToken = ''
    state.authorization = null
    state.environment = ''
    state.currentStepIndex = 0
    state.mobileFlowError = null
    state.mobileFlowActive = false
    state.mobileHandoffAuthenticated = false

    state.vaultPin = ''
    state.vaultMnemonic = ''
    state.vault = null
    state.vaultWallet = null
    state.vaultId = ''
    state.vaultSalt = ''
    state.authServerAuthToken = ''

    state.hasLivelinessDone = false
    state.hasKycDone = false
    state.hasSbtMintDone = false
    state.kycExtractedData = {}
    state.finalResults = false
    state.livelinessResult = {}
    state.ocrIDDocResult = {}
    state.extractionToken = {}
    state.finalResult = {}
    state.rescanFlag = false
    state.livelinessCapturedData = {
      tokenSelfiImage: '',
      biometricTemplateRaw: '',
      bestImageTokenized: '',
    }
    state.kycCapturedData = {
      tokenFrontDocumentImage: '',
      tokenBackDocumentImage: '',
      tokenFaceImage: '',
      countryCode: '',
    }
    state.userPresentationConsent = {}
  },

  setOnChainIssuerConfig: (state, payload) => {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.ON_CHAIN_CONFIG, JSON.stringify(payload))
  },
  setCosmosConnection: (state, payload) => {
    state.cosmosConnection = { ...payload }
  },
  setSteps: (state, payload) => {
    state.steps = payload
  },
  setTrustedSchemaIdsAndIssuers: (state, schemaIds) => {
    state.schemaIds = schemaIds
  },
  setKycServiceUserAccessToken: (state, payload) => {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.KYC_SERVICE_USER_ACCESS_TOKEN, payload)
  },
  setAuthorization: (state, authorization) => {
    state.authorization = authorization
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.AUTHORIZATION, authorization)
  },

  setSession(state, payload) {
    if (!payload || payload === 'undefined' || payload === 'null') {
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.SESSIONS)
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION)
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_STEP)
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.SESSION_EXPIRES_AT)
      return
    }

    const mobileFlowSession = localStorage.getItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION)
    if (mobileFlowSession && mobileFlowSession !== payload) {
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION)
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_STEP)
    }
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.SESSIONS, payload)
  },

  setMobileFlowSession(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.SESSIONS, payload)
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.MOBILE_FLOW_SESSION, payload)
    localStorage.removeItem(SignStoreConfig.LOCAL_STATES.SESSION_EXPIRES_AT)
  },

  setSessionExpiresAt(state, payload) {
    console.log(state.hasKycDone)
    if (payload) {
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.SESSION_EXPIRES_AT, payload)
    } else {
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.SESSION_EXPIRES_AT)
    }
  },

  setCavachAccessToken(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.KYC_ACCESS_TOKEN, payload)
  },
  setRedirectUrl(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.REDIRECT_URL, payload)
  },
  setPresentationRequest(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.PR, payload)
  },

  setSSIAccessToken(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.SSI_ACCESS_TOKEN, payload)
  },
  setEnv(state, payload) {
    state.environment = payload
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.ENVIRONMENT, payload)
  },

  setThridPartyAuth(state, payload) {
    state.authenticationAccessToken = { ...payload }
  },

  setProfile(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.PROFILE, JSON.stringify(payload))
  },

  setAsNewUser(state, payload) {
    state.ifNewUser = payload
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.IS_NEW_USER, payload)
  },

  setTenantSubdomain(state, payload) {
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.SUBDOMAIN, payload)
  },

  setIdDocumentLicenseKey(state, payload) {
    state.idDocumentLicenseKey = payload
  },

  setWidgetConfigFromDb: (state, payload) => {
    state.widgetConfigFromDb = { ...payload }
    localStorage.setItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG, JSON.stringify(payload))
  },
  setWidgetConfigId: (state, payload) => {
    const widgetConfigId = typeof payload === 'string' ? payload.trim() : ''
    if (widgetConfigId) {
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG_ID, widgetConfigId)
    } else {
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.WIDGET_CONFIG_ID)
    }
  },

  setMobileFlowError(state, payload) {
    state.mobileFlowError = payload
  },

  setMobileFlowActive(state, payload) {
    state.mobileFlowActive = payload
  },

  setMobileHandoffAuthenticated(state, payload) {
    state.mobileHandoffAuthenticated = payload
  },

  setUtmSource(state, payload) {
    if (payload) {
      localStorage.setItem(SignStoreConfig.LOCAL_STATES.UTM_SOURCE, payload)
    } else {
      localStorage.removeItem(SignStoreConfig.LOCAL_STATES.UTM_SOURCE)
    }
  },
}
